<template>
  <component
    :is="tag"
    :class="[
      'tw:rounded-full tw:m-0 tw:inline-flex tw:items-center tw:flex-shrink-0',
      text ? 'tw:px-3 tw:py-2' : 'tw:p-2',
      stateClass,
      skeleton && 'skeleton-play',
    ]"
    :style="badgeStyle"
    @click="emit('click')"
  >
    <slot>{{ text }}</slot>
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

const emit = defineEmits<{ click: [] }>()

const props = withDefaults(defineProps<{
  tag?: 'p' | 'span' | 'label' | 'strong' | 'div'
  text?: string | number
  size?: 'xxs' | 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl'
  color?: string
  bgColor?: string
  weight?: 'thin' | 'extralight' | 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold' | 'black'
  skeleton?: boolean
  state?: 'default' | 'danger' | 'warning' | 'success'
  decoration?: 'none' | 'underline' | 'line-through'
}>(), {
  tag: 'span',
  text: undefined,
  size: 'xs',
  color: undefined,
  bgColor: undefined,
  weight: 'semibold',
  skeleton: false,
  state: 'default',
  decoration: 'none',
})

const FONT_SIZES: Record<string, string> = {
  xxs: '0.625rem',
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
}

const WEIGHTS: Record<string, string> = {
  thin: '100', extralight: '200', light: '300', normal: '400',
  medium: '500', semibold: '600', bold: '700', extrabold: '800', black: '900',
}

const STATE_COLORS: Record<string, { bg: string; text: string }> = {
  default: { bg: 'var(--badge-bg-default)', text: '' },
  danger: { bg: 'var(--feedback-error-200)', text: 'var(--feedback-error-700)' },
  warning: { bg: 'var(--feedback-warning-200)', text: 'var(--feedback-warning-700)' },
  success: { bg: 'var(--feedback-success-200)', text: 'var(--feedback-success-700)' },
}

const stateClass = computed(() => {
  // Only apply state bg via class if no custom bgColor is set
  if (props.bgColor) return ''
  return ''
})

const badgeStyle = computed(() => {
  const stateColors = STATE_COLORS[props.state] ?? STATE_COLORS.default
  const s: Record<string, string> = {
    fontSize: FONT_SIZES[props.size] ?? '0.75rem',
    fontWeight: WEIGHTS[props.weight] ?? '600',
  }

  s.backgroundColor = props.bgColor ?? stateColors.bg
  s.color = props.color ?? stateColors.text ?? 'var(--fontcolor)'

  if (props.decoration !== 'none') {
    s.textDecoration = props.decoration
  }

  return s
})
</script>
